import React from 'react';
import { Link } from 'react-router-dom';
import './Products.css';
import PageHero from './PageHero';
import { IconCheck } from './Icons';

const quoteLink = (topic) => `/contact?reason=quote&topic=${encodeURIComponent(topic)}`;

/* ---- Website tiers ------------------------------------------------------- */

const TIERS = [
  {
    key: 'starter',
    name: 'Starter',
    price: 'from $700',
    timeline: '3–5 days',
    tagline:
      'A clean, fast, mobile-ready site for businesses that need to look legitimate online and be findable on Google.',
    features: [
      'Up to 3 pages',
      'Built on a proven layout, styled to your brand',
      'Mobile and tablet responsive',
      'Contact form wired to your inbox',
      'Google Analytics + search setup',
      'Domain connection and launch',
      '2 rounds of revisions',
    ],
    bestFor:
      'New businesses, single-service operations, anyone replacing a Facebook page as their only web presence.',
    topic: 'Starter website (from $700)',
  },
  {
    key: 'standard',
    name: 'Standard',
    price: 'from $1,850',
    timeline: '1–2 weeks',
    featured: true,
    badge: 'Most chosen',
    inherits: 'Everything in Starter, plus',
    tagline:
      'A fully custom site designed around your business, not adapted from a template.',
    features: [
      '4–6 custom-designed pages',
      'Original layout and design system built for your brand',
      'Copywriting support for every page',
      'Service or portfolio sections',
      'Local SEO setup (Google Business Profile, schema, location pages)',
      'Speed and Core Web Vitals optimization',
      '3 rounds of revisions',
    ],
    bestFor:
      'Established local businesses — contractors, shops, studios, service companies — that get real customers from their website.',
    note: 'Drops to $1,395 with a 12-month Care Plan',
    noteHref: '#care',
    topic: 'Standard website (from $1,850)',
  },
  {
    key: 'signature',
    name: 'Signature',
    price: 'from $3,100',
    timeline: '2–4 weeks',
    inherits: 'Everything in Standard, plus',
    tagline: 'The motion and interactive work we’re known for.',
    features: [
      'Custom 3D or scroll-driven hero built for your business',
      'Original animation and interaction design',
      'Cinematic entry sequence',
      'Custom illustration or 3D asset creation as needed',
      'Advanced performance tuning so it still loads fast',
      '3 rounds of revisions',
    ],
    bestFor:
      'Brands where the website is the first impression — production companies, premium services, anyone who needs to look like the most serious option in their market.',
    topic: 'Signature website (from $3,100)',
  },
];

/* ---- Add-ons ------------------------------------------------------------- */

const ADDONS = [
  { name: 'Logo design + full file set', detail: 'SVG, PNG, print-ready', price: 'from $450' },
  { name: 'Logo animation', price: 'from $400' },
  { name: 'Additional page', detail: 'after launch', price: '$250' },
  { name: 'E-commerce setup', price: 'from $950' },
  { name: 'Booking or scheduling integration', price: 'from $450' },
  { name: 'Extra revision round', price: '$250' },
  { name: 'Small changes and one-off jobs', price: '$75/hr' },
];

/* ---- Care plan ----------------------------------------------------------- */

const CARE = [
  'Hosting and domain management',
  'SSL, security monitoring, and backups',
  'Software and dependency updates',
  'Up to 30 minutes of content changes each month',
  'Monthly traffic report',
  'Priority response — we answer you first',
];

/* ---- Marketing ----------------------------------------------------------- */

const MARKETING = [
  {
    name: 'Social Media Management',
    price: 'from $450/month',
    features: [
      '12 posts per month across 2 platforms',
      'Custom graphics and short-form video editing',
      'Caption writing and hashtag strategy',
      'Scheduling and publishing',
      'Monthly performance report',
    ],
    footnote: 'Additional platforms and higher post volume priced on scope.',
    topic: 'Social media management (from $450/mo)',
  },
  {
    name: 'Meta Ads Management',
    price: 'from $250/month',
    lede:
      'Facebook and Instagram campaign management. Our fee is separate from your ad budget, which you pay Meta directly.',
    features: [
      'Campaign strategy and audience targeting',
      'Ad creative — graphics, video, and copy',
      'Pixel and conversion tracking setup',
      'Ongoing testing and optimization',
      'Monthly reporting',
    ],
    footnote:
      '$250/month flat for ad budgets under $2,000/month. Above $2,000/month, 15% of ad spend.',
    topic: 'Meta ads management (from $250/mo)',
  },
];

/* ---- Payment ------------------------------------------------------------- */

const PAYMENT = [
  {
    title: 'Deposit',
    body: 'A third down to start, a third at design approval, a third at launch.',
  },
  {
    title: 'Payment plans',
    body: 'We can split any project into three monthly payments at no extra cost. Ask and we’ll set it up.',
  },
  {
    title: 'What we need from you',
    body: 'Your logo files if you have them, any photos you want used, and your business details. If you don’t have photos or copy, we’ll tell you upfront what it costs to handle that too.',
  },
];

/* ---- FAQ ----------------------------------------------------------------- */

const FAQ = [
  {
    q: 'Why is this so much less than other agencies?',
    a: 'We’re a small team using modern tooling, so we build in days what larger shops schedule in weeks. We’re not cutting the work — we’re cutting the overhead and the timeline, and we pass that on.',
  },
  {
    q: 'Are these final prices?',
    a: 'They’re starting prices. Every project is quoted after we understand the scope. What you see here is the floor for each tier, and most local projects land at or near it.',
  },
  {
    q: 'Do I own the site?',
    a: 'Yes. Code, design, and domain are yours. If you ever leave, we hand everything over — no hostage situation.',
  },
  {
    q: 'What if I need changes after launch?',
    a: 'Small stuff is covered by the Care Plan. Bigger changes are quoted individually or billed at $75/hr.',
  },
  {
    q: 'How long does it take?',
    a: 'Starter: 3–5 days. Standard: 1–2 weeks. Signature: 2–4 weeks. The main variable is how fast you get us content and feedback.',
  },
  {
    q: 'Do you work with clients outside the Corning area?',
    a: 'Yes. We work remotely with clients anywhere. Pricing for larger or out-of-market projects is quoted on scope.',
  },
];

/* -------------------------------------------------------------------------
   Page
   ------------------------------------------------------------------------- */

const Products = () => (
  <>
    <PageHero
      eyebrow="Pricing"
      title="Clear numbers, no discovery call required."
      lede="We’re a Corning-based studio that builds custom websites — not templates dressed up as custom work. Before we design anything, we learn your business: who your customers are, why they pick you, and what the site actually needs to do. That’s the part most cheap website services skip, and it’s the reason our sites bring in work instead of just sitting there."
      meta={['Most projects ship in 1–2 weeks', 'Fixed quote within 48 hours', 'You own everything']}
    >
      <Link to="/start" className="btn btn--primary">
        Get started — $100 credit <span className="btn-arrow">→</span>
      </Link>
      <a href="#websites" className="btn btn--ghost">
        See the numbers
      </a>
    </PageHero>

    <main className="main-content products-page" id="main">
      {/* ---- Website tiers ------------------------------------------------ */}
      <section className="section products-headline" id="websites">
        <div className="container">
          <div className="section-head">
            <p className="eyebrow">Websites</p>
            <h2 className="section-title reveal reveal-up">Three tiers. Pick your floor.</h2>
            <p className="section-lede reveal reveal-up" style={{ '--reveal-delay': '0.08s' }}>
              Every number below is a starting price, and most local projects land at or
              near it. Anything larger gets quoted on scope.
            </p>
          </div>

          <div className="headline-grid" data-stagger="0.08">
            {TIERS.map((tier) => (
              <article
                className={`headline-card reveal reveal-up${tier.featured ? ' headline-card--featured' : ''}`}
                key={tier.name}
              >
                {tier.badge && <span className="headline-card__badge">{tier.badge}</span>}

                <h3 className="headline-card__name">{tier.name}</h3>

                <div className="headline-card__price">
                  <span className="headline-card__price-value">{tier.price}</span>
                  <span className="headline-card__price-note">Ships in {tier.timeline}</span>
                </div>

                <p className="headline-card__tagline">{tier.tagline}</p>

                {tier.inherits && <p className="tier-inherits">{tier.inherits}</p>}

                <ul className="headline-card__points">
                  {tier.features.map((feature) => (
                    <li key={feature}>
                      <IconCheck />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <p className="tier-best-for">
                  <span>Best for</span>
                  {tier.bestFor}
                </p>

                {tier.note && (
                  <a className="tier-note" href={tier.noteHref}>
                    {tier.note}
                    <span className="btn-arrow">↓</span>
                  </a>
                )}

                <Link
                  to={`/start?package=${tier.key}`}
                  className={`btn btn--block ${tier.featured ? 'btn--primary' : 'btn--ghost'}`}
                >
                  Get started <span className="btn-arrow">→</span>
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ---- Add-ons ------------------------------------------------------- */}
      <section className="section section--alt addons-section" id="addons">
        <div className="container">
          <div className="section-head">
            <p className="eyebrow">Add-ons</p>
            <h2 className="section-title reveal reveal-up">Bolt anything on.</h2>
          </div>

          <div className="addons-table reveal reveal-up">
            <div className="addons-head">
              <span>Service</span>
              <span>Price</span>
            </div>
            <ul>
              {ADDONS.map((addon) => (
                <li key={addon.name}>
                  <span className="addon-name">
                    {addon.name}
                    {addon.detail && <em>{addon.detail}</em>}
                  </span>
                  <span className="addon-price">{addon.price}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ---- Care plan ----------------------------------------------------- */}
      <section className="section care-section" id="care">
        <div className="container care-grid">
          <div className="care-copy">
            <p className="eyebrow">Care Plan</p>
            <h2 className="section-title reveal reveal-up">
              Websites aren’t finished at launch.
            </h2>
            <p className="section-lede reveal reveal-up" style={{ '--reveal-delay': '0.08s' }}>
              The Care Plan keeps yours online, current, and working. No contract —
              cancel any time.
            </p>

            <div className="care-bundle reveal reveal-up" style={{ '--reveal-delay': '0.14s' }}>
              <span className="care-bundle-label">Launch Bundle</span>
              <p>
                Sign up for 12 months of Care with your build and the Standard package
                drops to <strong>$1,395</strong>.
              </p>
            </div>
          </div>

          <div className="care-card reveal reveal-scale">
            <div className="care-price">
              <span className="care-price-value">$150</span>
              <span className="care-price-unit">/month</span>
            </div>
            <ul className="care-list">
              {CARE.map((item) => (
                <li key={item}>
                  <IconCheck />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <Link to={quoteLink('Care Plan ($150/mo)')} className="btn btn--primary btn--block">
              Start a Care Plan <span className="btn-arrow">→</span>
            </Link>
          </div>
        </div>
      </section>

      {/* ---- Marketing ------------------------------------------------------ */}
      <section className="section section--alt marketing-section" id="marketing">
        <div className="container">
          <div className="section-head">
            <p className="eyebrow">Marketing</p>
            <h2 className="section-title reveal reveal-up">Once the site is up.</h2>
          </div>

          <div className="marketing-grid" data-stagger="0.08">
            {MARKETING.map((service) => (
              <article className="marketing-card reveal reveal-up" key={service.name}>
                <div className="marketing-card__top">
                  <h3>{service.name}</h3>
                  <span className="marketing-card__price">{service.price}</span>
                </div>

                {service.lede && <p className="marketing-card__lede">{service.lede}</p>}

                <ul>
                  {service.features.map((feature) => (
                    <li key={feature}>
                      <IconCheck />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <p className="marketing-card__footnote">{service.footnote}</p>

                <Link to={quoteLink(service.topic)} className="btn btn--ghost btn--sm">
                  Get a quote <span className="btn-arrow">→</span>
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ---- Payment -------------------------------------------------------- */}
      <section className="section payment-section">
        <div className="container">
          <div className="section-head">
            <p className="eyebrow">How payment works</p>
            <h2 className="section-title reveal reveal-up">No surprises on the invoice.</h2>
          </div>

          <ol className="payment-list" data-stagger="0.07">
            {PAYMENT.map((step, idx) => (
              <li className="payment-item reveal reveal-up" key={step.title}>
                <span className="payment-index">{String(idx + 1).padStart(2, '0')}</span>
                <div>
                  <h3>{step.title}</h3>
                  <p>{step.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ---- FAQ ------------------------------------------------------------ */}
      <section className="section section--alt faq-section">
        <div className="container">
          <div className="section-head">
            <p className="eyebrow">Common questions</p>
            <h2 className="section-title reveal reveal-up">The things people ask first.</h2>
          </div>

          <div className="faq-list reveal reveal-up">
            {FAQ.map((item) => (
              <details className="faq-item" key={item.q}>
                <summary>
                  <span>{item.q}</span>
                  <span className="faq-marker" aria-hidden="true" />
                </summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ---- Closing CTA ----------------------------------------------------- */}
      <section className="section products-footnote-section">
        <div className="container container--narrow products-footnote">
          <p className="eyebrow eyebrow--plain">Ready to start?</p>
          <h2 className="section-title reveal reveal-up">Tell us about your business.</h2>
          <p className="section-lede reveal reveal-up" style={{ '--reveal-delay': '0.08s' }}>
            We’ll send a fixed quote within 48 hours — and if a cheaper tier would
            serve you better, we’ll say so.
          </p>
          <div className="btn-row reveal reveal-up" style={{ '--reveal-delay': '0.16s' }}>
            <Link to="/start" className="btn btn--primary btn--lg">
              Get your fixed quote — $100 credit <span className="btn-arrow">→</span>
            </Link>
            <Link to="/live-apps" className="btn btn--ghost btn--lg">
              See live work
            </Link>
          </div>
        </div>
      </section>
    </main>
  </>
);

export default Products;
